import React, { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";
//Limitless imports
import { ClientContext } from "@limitless/common/utils";
import { useWebSockets } from "@limitless/common/hooks/web-sockets/WebSocketsContext";
import { useCustomerContext, useCustomerMessageContext, useGroupResponseTimeContext } from "@limitless/customer/hooks";
import { createLLMWindowObject } from "utils/LLMWindowObject";

const AppVisibilityContext = createContext({});

export const AppVisibilityContextProvider = ({ children }) => {
    const hook = useAppVisibility();

    const context = useMemo(
        () => ({
            ...hook
        }),
        [hook.appVisibility, hook.unReadMessage, hook.inputFocus]
    );

    return <AppVisibilityContext.Provider value={context}>{children}</AppVisibilityContext.Provider>;
};

export const useAppVisibilityContext = () => {
    return useContext(AppVisibilityContext);
};

AppVisibilityContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};

/**
 * Mobile Detection hook only accessed via the context wrapper so isMobileDevice can be made across the whole app
 */
const useAppVisibility = () => {
    const [appVisibility, setAppVisibility] = React.useState({
        isAppVisible: ClientContext._appVisible,
        isAppOpen: false,
        hasOpened: false
    });
    const [unReadMessage, setUnReadMessage] = React.useState(false);
    const [inputFocus, setInputFocus] = React.useState(false);
    const [lastMessageUpdate, setLastMessageUpdate] = React.useState(null);
    const [hasConnectedToWS, setHasConnectedtoWS] = React.useState(false);
    const { clearQuestion } = useCustomerContext();
    const { message } = useCustomerMessageContext();
    const { loadResponseTime } = useGroupResponseTimeContext();
    const { connectToWebSocket } = useWebSockets();

    const toggleAppOpen = () => {
        // check if we have an unread message
        if (unReadMessage) {
            setUnReadMessage(false);
        }

        setAppVisibility({ isAppVisible: true, isAppOpen: !appVisibility.isAppOpen, hasOpened: true });
    };

    const toggleAppVisibility = isVisible => {
        setAppVisibility({ ...appVisibility, isAppVisible: isVisible, isAppOpen: false });
    };

    const openApp = () => {
        setAppVisibility({ isAppVisible: true, isAppOpen: true, hasOpened: true });
    };

    const clearUnreadMessage = () => {
        setUnReadMessage(false);
    };

    const toggleInputFocus = value => {
        setInputFocus(value);
    };

    React.useEffect(() => {
        createLLMWindowObject(appVisibility, toggleAppVisibility, openApp);
    }, []);

    React.useEffect(() => {
        //if we close the App, clear the current question due to old requirements..
        //and save the last time the current message was updated
        if (!appVisibility.isAppOpen && appVisibility.hasOpened) {
            clearQuestion();
            setLastMessageUpdate(message.updated);
        }

        if (appVisibility.isAppOpen) {
            loadResponseTime();
            if (!hasConnectedToWS) {
                setHasConnectedtoWS(true);
                connectToWebSocket();
            }
        }
    }, [appVisibility.isAppOpen]);

    React.useEffect(() => {
        //if the app isn't open and a new dialogue comes in, set the unread flag
        if (!appVisibility.isAppOpen && lastMessageUpdate < message.updated) {
            setUnReadMessage(true);
        }
    }, [message.updated]);

    return {
        appVisibility,
        toggleAppOpen,
        unReadMessage,
        clearUnreadMessage,
        inputFocus,
        toggleInputFocus,
        toggleAppVisibility
    };
};
